import axios, { AxiosResponse } from 'axios'
import {
  DocumentoPessoaType,
  ID,
  SuccessResponse,
  getApiErrorMessage,
} from '../../../../_metronic/helpers'
import {
  Picture,
  AuthTokenResponse,
  CertificadoDigitalDto,
  TokenDtoRefresh,
  TokenDtoSignature,
  TokenDtoValidate,
  PasswordValidate,
  VideoChamadaToken,
  PageResponsePessoaUsuarioList,
  AvatarDto,
  UsuarioDtoLogin,
  UsuarioDtoLogout,
  PasswordDtoRecover,
  EmpresaDtoPublica,
  MfaDtoSetup,
  MfaDtoValidate,
  MfaSetup,
  Fator2faStatus,
  CodigosRecuperacaoResponse,
  LoginResponse,
} from './_models'
import Swal from 'sweetalert2'
import { EnderecoList } from '../../apps/empresa/empresas-list/core/_models'
import { Estado } from '../../apps/atendimento_medicos/atendimentos-medico-list/core/_models'
import {
  UsuarioDtoVinculo,
  UsuarioView,
} from 'app/modules/apps/admin/usuarios/usuarios-list/core/_models'

const API_URL = process.env.REACT_APP_API_URL

export const GET_USER_BY_ACCESSTOKEN_URL = `${API_URL}/usuarios/me`
export const LOGIN_URL = `${API_URL}/auth/login`
export const REGISTER_URL = `${API_URL}/register`
export const REQUEST_PASSWORD_URL = `${API_URL}/auth/forgot-password`
export const CONFIRM_ACCOUNT_URL = `${API_URL}/auth/confirm-account`
export const USUARIO_URL = `${API_URL}/usuarios`
export const USUARIO_PICTURE_URL = `${API_URL}/usuarios/picture`
export const USUARIO_AVATAR_URL = `${API_URL}/usuarios/avatar`
export const USUARIO_CERTIFICADO_URL = `${API_URL}/usuarios/certificado`
export const TOKEN_URL = `${API_URL}/token`
export const MFA_URL = `${API_URL}/auth/mfa`
export const MFA_SETUP_URL = `${API_URL}/auth/mfa-setup`
export const USUARIO_2FA_URL = `${API_URL}/usuarios/me/2fa`

/**
 * Autentica o usuário. A resposta é a sessão emitida OU um desafio de segundo fator — use
 * {@link isMfaDesafio} para distinguir antes de tratar o retorno como sessão.
 * @param data UsuarioDtoLogin
 * @returns LoginResponse
 */
export const login = (data: UsuarioDtoLogin): Promise<LoginResponse> => {
  return axios.post(LOGIN_URL, data).then((resp: AxiosResponse<LoginResponse>) => resp.data)
}

/**
 * Desautentica usuário
 * @param data UsuarioDtoLogout — se `pushToken` vier, revoga só o token deste dispositivo;
 * sem `pushToken`, o backend revoga todos os tokens push da pessoa.
 * @returns void
 */
export const logoutUser = (data?: UsuarioDtoLogout): Promise<void> => {
  return axios.delete(LOGIN_URL, { data })
}

export const requestPassword = (data: PasswordDtoRecover): Promise<SuccessResponse> => {
  return axios
    .post(REQUEST_PASSWORD_URL, data)
    .then((resp: AxiosResponse<SuccessResponse>) => resp.data)
}

// Server should return object => { result: boolean } (Is Email in DB)
export function resetPasswordUser(
  id: String,
  senhaAtual: string,
  novaSenha: string,
  confirmaNovaSenha: string
) {
  return axios.put<{ result: boolean }>(USUARIO_URL + '/password', {
    id,
    senhaAtual,
    novaSenha,
    confirmaNovaSenha,
  })
}

export const confirmAccount = (data: TokenDtoValidate) => {
  return axios.post(CONFIRM_ACCOUNT_URL, data)
}

/**
 * Gera o segredo do autenticador durante o cadastro obrigatório no login, usando o token de
 * desafio no lugar de uma sessão — que ainda não existe neste ponto.
 */
export const gerarSegredo2fa = (data: MfaDtoSetup): Promise<MfaSetup> => {
  return axios.post(MFA_SETUP_URL, data).then((resp: AxiosResponse<MfaSetup>) => resp.data)
}

/**
 * Valida o segundo fator e conclui o login. Aceita tanto o código do autenticador quanto um
 * código de recuperação. Quando o fator acabou de ser cadastrado, a resposta traz os códigos
 * de recuperação — a única vez em que eles trafegam.
 */
export const validar2fa = (data: MfaDtoValidate): Promise<AuthTokenResponse> => {
  return axios.post(MFA_URL, data).then((resp: AxiosResponse<AuthTokenResponse>) => resp.data)
}

export const buscarStatus2fa = (): Promise<Fator2faStatus> => {
  return axios.get(USUARIO_2FA_URL).then((resp: AxiosResponse<Fator2faStatus>) => resp.data)
}

export const iniciar2fa = (): Promise<MfaSetup> => {
  return axios.post(USUARIO_2FA_URL).then((resp: AxiosResponse<MfaSetup>) => resp.data)
}

export const ativar2fa = (codigo: string): Promise<CodigosRecuperacaoResponse> => {
  return axios
    .put(USUARIO_2FA_URL, { codigo })
    .then((resp: AxiosResponse<CodigosRecuperacaoResponse>) => resp.data)
}

export const desativar2fa = (codigo: string): Promise<void> => {
  return axios.delete(USUARIO_2FA_URL, { data: { codigo } })
}

export const regenerarCodigos2fa = (codigo: string): Promise<CodigosRecuperacaoResponse> => {
  return axios
    .post(`${USUARIO_2FA_URL}/codigos`, { codigo })
    .then((resp: AxiosResponse<CodigosRecuperacaoResponse>) => resp.data)
}

export function updateEmailUser(id: ID, email: string) {
  return axios.put<{ result: boolean }>(USUARIO_URL + '/' + { id }, {
    email: email,
  })
}

export async function persistCertificado(password: string, certificadoValue?: File) {
  if (!certificadoValue) {
    Swal.fire({
      text: 'Arquivo não informado',
      icon: 'error',
      buttonsStyling: false,
      confirmButtonText: 'Ok',
      customClass: {
        confirmButton: 'btn btn-primary',
      },
    })
    return
  }

  try {
    const base64Certificado = await new Promise<string | ArrayBuffer | null>((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(certificadoValue)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (e) => reject(e)
    })

    if (!base64Certificado) {
      throw new Error('Falha ao ler o arquivo de certificado.')
    }

    await editCertificado(
      'u',
      base64Certificado,
      certificadoValue.name,
      certificadoValue.type,
      password
    )
  } catch (error: any) {
    Swal.fire({
      text: getApiErrorMessage(error) || 'Erro ao processar o certificado.',
      icon: 'error',
      buttonsStyling: false,
      confirmButtonText: 'Ok',
      customClass: {
        confirmButton: 'btn btn-primary',
      },
    })
    throw error
  }
}

/**
 * Altera ou remove certificado digital de usuário
 * @param idUsuario Identificador do Usuário
 * @param certificado CertificadoDigitalDto
 * @returns UsuarioView
 */
export const editCertificadoByUsuario = async (
  idUsuario: ID,
  certificado: CertificadoDigitalDto
): Promise<UsuarioView> => {
  return axios
    .post(`${USUARIO_URL}/${idUsuario}/certificado `, certificado)
    .then((resp: AxiosResponse<UsuarioView>) => resp.data)
}

function editCertificado(
  action: string,
  fileContent: any,
  fileName: string,
  fileType: string,
  password: string
) {
  return axios.post<{ result: boolean }>(USUARIO_CERTIFICADO_URL, {
    action: action,
    fileContent: fileContent,
    fileName: fileName,
    fileType: fileType,
    senha: password,
  })
}

export const editAvatarUser = (data: AvatarDto): Promise<UsuarioView> => {
  return axios.post(USUARIO_AVATAR_URL, data).then((resp: AxiosResponse<UsuarioView>) => resp.data)
}

export function editImageUser(data: any) {
  return axios.post<{ result: boolean }>(
    USUARIO_PICTURE_URL + 'action=u',
    {
      file: data,
    },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )
}

/**
 * Busca avatar do usuário autenticado
 * @returns Avatar - string
 */
export const getAvatarUser = (): Promise<string> => {
  return axios
    .get(USUARIO_AVATAR_URL)
    .then((resp: AxiosResponse<{ data: string }>) => resp.data.data)
}
export function reenvioTokenConfirm(id: ID) {
  return axios.put(USUARIO_URL + '/' + id + '/confirma')
}

export const getAvatarUserById = (id: ID): Promise<Picture | undefined> => {
  return axios
    .get(`${USUARIO_URL}/${id}/avatar`)
    .then((response: AxiosResponse<Picture>) => response.data)
}

export function getUserByCPF(cpf: string) {
  return axios.get<UsuarioDtoVinculo>(`${USUARIO_URL}/cpf/${cpf}`)
}

/**
 * Busca usuário de pessoa
 * @param idPessoa ID de Pessoa
 * @returns UsuarioDtoVinculo
 */
export function getUserByPessoa(idPessoa: ID): Promise<UsuarioDtoVinculo> {
  return axios
    .get(`${USUARIO_URL}/pessoa/${idPessoa}`)
    .then((resp: AxiosResponse<UsuarioDtoVinculo>) => resp.data)
}
/**
 * Busca o usuario autenticado
 * @returns UsuarioDtoVinculo
 */
export const getLoggedUser = () => {
  return axios
    .get<UsuarioDtoVinculo>(GET_USER_BY_ACCESSTOKEN_URL)
    .then((resp: AxiosResponse<UsuarioDtoVinculo>) => resp.data)
}

/**
 *
 * @param tipoDocumento DocumentoPessoaType
 * @param numero string do documento selecionado
 * @returns UsuarioDtoVinculo
 */
export const getUserByDocumento = (tipoDocumento: DocumentoPessoaType, numero: string) => {
  return axios
    .get(`${USUARIO_URL}/documento/${tipoDocumento}/${numero}`)
    .then((response: AxiosResponse<UsuarioDtoVinculo>) => response.data)
}

/**
 * Busca usuário da pessoa
 * @param pessoaId Identificador da pessoa
 * @returns UsuarioDtoVinculo
 */
export const getUsuarioByPessoaId = (pessoaId: number): Promise<UsuarioDtoVinculo> => {
  return axios
    .get(`${API_URL}/usuarios/pessoa/${pessoaId}`)
    .then((resp: AxiosResponse<UsuarioDtoVinculo>) => resp.data)
}

/**
 * Gera senha automática e envia ao usuário
 * @param usuarioId ID do usuário
 * @returns SuccessResponse
 */
export const alterarSenha = (usuarioId: number): Promise<SuccessResponse> => {
  return axios
    .patch(`${API_URL}/usuarios/${usuarioId}/password`)
    .then((resp: AxiosResponse<SuccessResponse>) => resp.data)
}

/**
 * Força troca de senha do usuario
 * @param usuarioId ID do usuário
 * @returns SuccessResponse
 */
export const inativarSenha = (usuarioId: number): Promise<SuccessResponse> => {
  return axios
    .delete(`${API_URL}/usuarios/${usuarioId}/password`)
    .then((resp: AxiosResponse<SuccessResponse>) => resp.data)
}

/**
 * Busca endereços da pessoa
 * @param pessoaId Identificador da Pessoa
 * @returns EnderecoList[]
 */
export const getEnderecosByPessoaId = (pessoaId: number): Promise<EnderecoList[]> => {
  return axios
    .get(`${API_URL}/enderecos/pessoa/${pessoaId}`)
    .then((response: AxiosResponse<EnderecoList[]>) => response.data)
}

/**
 * Busca todas UF
 * @returns Estado[]
 */
export const getEstadosPublic = (): Promise<Estado[]> => {
  return axios
    .get(`${API_URL}/public/uf`)
    .then((response: AxiosResponse<Estado[]>) => response.data)
}

/**
 * Renova token
 * @param token TokenDtoRefresh
 * @returns AuthTokenResponse
 */
export const refreshToken = (token: TokenDtoRefresh): Promise<AuthTokenResponse> => {
  return axios
    .post(`${TOKEN_URL}/refresh`, token)
    .then((response: AxiosResponse<AuthTokenResponse>) => response.data)
}

/**
 * Renova token para assinatura
 * @param tokenSignature TokenDtoSignature
 * @returns AuthTokenResponse
 */
export const refreshTokenForSignature = (
  tokenSignature: TokenDtoSignature
): Promise<AuthTokenResponse> => {
  return axios
    .post(`${TOKEN_URL}/signature`, tokenSignature)
    .then((response: AxiosResponse<AuthTokenResponse>) => response.data)
}

/**
 * Valida token
 * @param token TokenDtoValidate
 * @returns PasswordValidate
 */
export const validateToken = (token: TokenDtoValidate): Promise<PasswordValidate> => {
  return axios
    .post(`${TOKEN_URL}/validate`, token)
    .then((response: AxiosResponse<PasswordValidate>) => response.data)
}

export const geraTokenAutenticacaoVideo = (atendimentoId: number): Promise<VideoChamadaToken> => {
  return axios
    .get(`${API_URL}/atendimentos/${atendimentoId}/video/token`)
    .then((resp: AxiosResponse<VideoChamadaToken>) => resp.data)
}

/**
 * Busca pessoas
 * @param query QueryState
 * @returns PageResponsePessoaUsuarioList
 */
export const getPessoas = (query: string): Promise<PageResponsePessoaUsuarioList> => {
  return axios
    .get(`${USUARIO_URL}/pessoa?${query}`)
    .then((response: AxiosResponse<PageResponsePessoaUsuarioList>) => response.data)
}

/**
 * Busca dado públicos da empresa
 * @param slug Slug da empresa
 * @returns EmpresaDtoPublica
 */
export const getSlugInfo = (slug: string): Promise<EmpresaDtoPublica> => {
  return axios
    .get(`${API_URL}/public/empresa/${slug}`)
    .then((response: AxiosResponse<EmpresaDtoPublica>) => response.data)
}
